import { useState, useEffect } from 'react'
import { Search, Award, Globe, RefreshCw, Loader2, Zap, ExternalLink, Tag, Rocket, Check, Star, TrendingUp, TrendingDown, Minus, Sparkles } from 'lucide-react'
import { api } from '../lib/api'
import { toast } from 'react-toastify'
import TrendModal from './TrendModal'
import TrendBadge from './TrendBadge'
import { useDemoMode } from '../hooks/usePreserveQueryParams'
import Modal from './Modal'

interface SampleQuery {
  query: string
  context: string
  mentioned: boolean
  position: number | null
  featured: boolean
  url?: string | null
  title?: string | null
  snippet?: string | null
  matchType?: string | null
  totalResults?: number
}

interface GoogleRankingData {
  score: number
  avgPosition: number | null
  searchFrequency: number
  featuredSnippets: number
  knowledgePanel: boolean
  totalQueriesAnalyzed: number
  lastAnalyzed: string
  sampleQueries?: SampleQuery[]
  scoreBreakdown?: {
    contextBreakdown?: Record<string, {
      mentionRate: number
      mentioned: number
      total: number
    }>
  }
}

interface GoogleRankingsCardProps {
  rankings: GoogleRankingData | null
  brandName?: string
  brandId: string
  onRefresh?: () => void
}

export default function GoogleRankingsCard({ rankings, brandName = '', brandId, onRefresh }: GoogleRankingsCardProps) {
  const [modalOpen, setModalOpen] = useState(false)
  const [showTrendModal, setShowTrendModal] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [checkingForJobs, setCheckingForJobs] = useState(true)
  const [processingJobId, setProcessingJobId] = useState<string | null>(null)
  const [jobStatus, setJobStatus] = useState<'waiting' | 'active' | 'completed' | 'failed' | null>(null)
  const [brandTrend, setBrandTrend] = useState<{trend: string, interestOverTime: number} | null>(null)
  const [historicalData, setHistoricalData] = useState<Array<{weekStartDate: string, avgPosition: number | null}>>([])
  const [queryTab, setQueryTab] = useState<'brand' | 'non-brand'>('brand')
  const [generatedNonBrandQueries, setGeneratedNonBrandQueries] = useState<Array<{query: string, reasoning: string, context: string, source?: string}>>([])
  const [generatingQueries, setGeneratingQueries] = useState(false)
  const { isDemoMode } = useDemoMode()

  // Fetch historical data when not in demo mode
  useEffect(() => {
    if (isDemoMode || !brandId) return

    const fetchHistory = async () => {
      try {
        const response = await api.get(`/visibility/search-rankings/${brandId}/history?limit=6`)
        if (response.data.success && response.data.data) {
          setHistoricalData(response.data.data)
        }
      } catch (error) {
        console.error('Error fetching search ranking history:', error)
      }
    }

    fetchHistory()
  }, [brandId, isDemoMode])

  // Prepare trend data from real historical data or demo data
  const avgPosition = rankings?.avgPosition || 0
  const trendData = isDemoMode || historicalData.length === 0
    ? [
        // Demo trend data (6 months of average position - lower is better)
        { date: new Date(Date.now() - 5 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: avgPosition * 1.4 },
        { date: new Date(Date.now() - 4 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: avgPosition * 1.3 },
        { date: new Date(Date.now() - 3 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: avgPosition * 1.2 },
        { date: new Date(Date.now() - 2 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: avgPosition * 1.1 },
        { date: new Date(Date.now() - 1 * 30 * 24 * 60 * 60 * 1000).toISOString(), value: avgPosition * 1.05 },
        { date: new Date(Date.now()).toISOString(), value: avgPosition }
      ]
    : [
        // Real historical data (sorted oldest to newest)
        ...historicalData
          .slice()
          .reverse()
          .map(item => ({
            date: item.weekStartDate,
            value: item.avgPosition || 0
          }))
      ]

  // Calculate month-over-month change (lower position = better, so inverse trend)
  const lastMonthValue = trendData[trendData.length - 2]?.value || avgPosition
  const monthOverMonthChange = avgPosition - lastMonthValue
  // Treat very small changes (< 0.1) as "stable" to avoid showing "- 0.0 pos" or "+ 0.0 pos"
  const trend = Math.abs(monthOverMonthChange) < 0.1 ? 'stable' : monthOverMonthChange < 0 ? 'up' : 'down'

  // Check for active job on mount
  useEffect(() => {
    const checkForActiveJob = async () => {
      try {
        setCheckingForJobs(true)
        const response = await api.get(`/visibility/search-rankings/${brandId}/active-job`)
        if (response.data.hasActiveJob) {
          setProcessingJobId(response.data.jobId)
          setJobStatus(response.data.status)
          setRefreshing(true)
        }
      } catch (error) {
        console.error('Error checking for active search ranking job:', error)
      } finally {
        setCheckingForJobs(false)
      }
    }

    checkForActiveJob()
  }, [brandId])

  // Poll for job status with timeout
  useEffect(() => {
    if (!processingJobId) return

    let pollCount = 0
    const maxPolls = 150 // 150 * 2s = 5 minutes max

    const pollInterval = setInterval(async () => {
      pollCount++

      // Timeout after 5 minutes
      if (pollCount >= maxPolls) {
        clearInterval(pollInterval)
        setProcessingJobId(null)
        setRefreshing(false)
        toast.error('Search ranking analysis timed out. The service may be unavailable.')
        console.warn('[GoogleRankings] Job polling timed out after 5 minutes')
        return
      }

      try {
        const response = await api.get(`/visibility/search-rankings/job/${processingJobId}/status`)
        const status = response.data.status

        setJobStatus(status)

        if (status === 'completed') {
          clearInterval(pollInterval)
          setProcessingJobId(null)
          setRefreshing(false)
          toast.success('Search rankings updated successfully!')

          if (onRefresh) {
            onRefresh()
          }
        } else if (status === 'failed') {
          clearInterval(pollInterval)
          setProcessingJobId(null)
          setRefreshing(false)
          toast.error('Search ranking analysis failed')
        }
      } catch (error) {
        console.error('Error checking job status:', error)
        // If API call fails multiple times, stop polling
        if (pollCount > 5) {
          clearInterval(pollInterval)
          setProcessingJobId(null)
          setRefreshing(false)
          toast.error('Unable to check job status. Please refresh the page.')
        }
      }
    }, 2000) // Poll every 2 seconds

    return () => clearInterval(pollInterval)
  }, [processingJobId, onRefresh])

  // Fetch brand trend data when rankings are available
  useEffect(() => {
    if (!brandId || !rankings) return

    const fetchTrends = async () => {
      try {
        const response = await api.get(`/visibility/search-rankings/${brandId}/trends`)
        if (response.data.success && response.data.data) {
          setBrandTrend(response.data.data)
        }
      } catch (error) {
        console.error('Error fetching trends data:', error)
      }
    }

    fetchTrends()
  }, [brandId, rankings])

  const handleRefreshRankings = async () => {
    try {
      setRefreshing(true)

      const response = await api.post(`/visibility/search-rankings/${brandId}/analyze`, { forceRefresh: true })

      const jobId = response.data.jobId
      setProcessingJobId(jobId)
      setJobStatus('waiting')

    } catch (error) {
      console.error('Error refreshing search rankings:', error)
      toast.error('Failed to refresh search rankings')
      setRefreshing(false)
    }
  }

  // Generate non-brand search queries
  const handleGenerateNonBrandQueries = async () => {
    try {
      setGeneratingQueries(true)

      const response = await api.post(`/visibility/search-rankings/${brandId}/generate-non-brand-queries`, { count: 10 })

      if (response.data.success && response.data.data?.queries) {
        setGeneratedNonBrandQueries(response.data.data.queries)
        toast.success(`Generated ${response.data.data.queries.length} non-brand queries!`)
      }
    } catch (error) {
      console.error('Error generating non-brand queries:', error)
      toast.error('Failed to generate queries')
    } finally {
      setGeneratingQueries(false)
    }
  }

  // Helper function to render brand trend indicator
  const renderBrandTrend = () => {
    if (!brandTrend) return 'No data'

    const { trend } = brandTrend

    if (trend === 'rising') {
      return (
        <span className="flex items-center gap-1 text-green-700">
          <TrendingUp className="w-4 h-4" />
          <span>Rising</span>
        </span>
      )
    } else if (trend === 'falling') {
      return (
        <span className="flex items-center gap-1 text-red-700">
          <TrendingDown className="w-4 h-4" />
          <span>Falling</span>
        </span>
      )
    } else if (trend === 'stable') {
      return (
        <span className="flex items-center gap-1 text-blue-700">
          <Minus className="w-4 h-4" />
          <span>Stable</span>
        </span>
      )
    } else if (trend === 'new') {
      return <span className="text-gray-500">New brand</span>
    }

    return <span className="text-gray-500">No data</span>
  }

  const contextLabels: Record<string, string> = {
    baseline: 'Brand Name',
    brand_paa: 'Google PAA',
    brand_related: 'Google Related',
    reviews: 'Brand Reviews',
    comparisons: 'Brand Comparisons',
    industry: 'Brand Info',
    products: 'Brand Products',
    news: 'Brand News',
    discovery: 'Discovery',
    fallback: 'Template'
  }

  return (
    <>
      {/* Card */}
      <div
        className="bg-gradient-to-br from-sky-50 to-white p-4 rounded-lg ring-1 ring-sky-200 space-y-2 cursor-pointer hover:ring-2 hover:ring-sky-300 transition-all"
        onClick={() => setModalOpen(true)}
      >
        {/* Row 1: Title + Key Metric */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Search className="w-4 h-4 text-sky-600" />
            <h3 className="text-sm font-semibold text-gray-900">Google Rankings</h3>
            <TrendBadge
              hasHistoricalData={true}
              trend={trend}
              value={Math.abs(monthOverMonthChange)}
              unit=" pos"
              decimals={1}
              onClick={() => setShowTrendModal(true)}
            />
          </div>
          <div className="text-2xl font-bold text-sky-600">
            {checkingForJobs || refreshing ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : typeof rankings?.avgPosition === 'number' ? (
              `#${rankings.avgPosition.toFixed(1)}`
            ) : rankings ? (
              'N/A'
            ) : (
              '—'
            )}
          </div>
        </div>

        {/* Row 2: Takeaway/Analysis */}
        <p className="text-xs text-gray-600 mb-2">
          {!rankings ? "No ranking data collected yet." : (() => {
            const pos = rankings?.avgPosition
            const hasPanel = rankings?.knowledgePanel
            if (hasPanel) return "Strong presence with Knowledge Panel visibility."
            if (pos === null) return "Not appearing in search results yet."
            if (pos <= 3) return "Excellent ranking - appearing in top results."
            if (pos <= 10) return "Good ranking - visible on first page."
            if (pos <= 20) return "Moderate ranking - appearing on second page."
            return "Low ranking - appearing outside top results."
          })()}
        </p>

        {/* Row 3: What was analyzed and result */}
        <p className="text-xs text-gray-500 mb-2">
          {checkingForJobs ? "Checking analysis status..." :
            refreshing ? "Analyzing Google search rankings..." :
            !rankings ? "Click to run Google rankings analysis" :
            typeof rankings.avgPosition === 'number'
            ? `Listed across ${rankings.totalQueriesAnalyzed ?? 0} Google brand searches`
            : `Not listed across ${rankings.totalQueriesAnalyzed ?? 0} Google brand searches`}
        </p>

        {/* Row 4: Brand vs Non-Brand breakdown */}
        {rankings?.sampleQueries && (() => {
          const brandedQueries = rankings.sampleQueries.filter(q =>
            brandName ? q.query.toLowerCase().includes(brandName.toLowerCase()) : false
          );
          const nonBrandedQueries = rankings.sampleQueries.filter(q =>
            brandName ? !q.query.toLowerCase().includes(brandName.toLowerCase()) : true
          );

          const brandedVisibility = brandedQueries.length > 0
            ? (brandedQueries.filter(q => q.mentioned).length / brandedQueries.length) * 100
            : 0;
          const nonBrandedVisibility = nonBrandedQueries.length > 0
            ? (nonBrandedQueries.filter(q => q.mentioned).length / nonBrandedQueries.length) * 100
            : 0;

          return (
            <div className="space-y-1">
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="text-gray-500">Brand:</span>
                <span className="font-medium text-sky-700">{brandedVisibility.toFixed(0)}% visible</span>
                {nonBrandedQueries.length > 0 && (
                  <>
                    <span className="text-gray-400">•</span>
                    <span className="text-gray-500">Non-Brand:</span>
                    <span className="font-medium text-sky-700">{nonBrandedVisibility.toFixed(0)}% visible</span>
                  </>
                )}
              </div>
            </div>
          )
        })()}
      </div>

      {/* Modal */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} size="5xl">
        <Modal.Header onClose={() => setModalOpen(false)}>
          <Search className="w-6 h-6 text-sky-600" />
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900">Search Rankings</h2>
            {!refreshing && !jobStatus && (
              <p className="text-sm text-gray-600 mt-1">
                Your brand's visibility in search engine results across {rankings?.totalQueriesAnalyzed || 0} brand-related queries
              </p>
            )}
          </div>
          {/* Refresh button only shown in development */}
          {import.meta.env.DEV && (
            <button
              onClick={handleRefreshRankings}
              disabled={refreshing}
              className="p-2 hover:bg-sky-50 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh search rankings (dev only)"
            >
              <RefreshCw className={`w-5 h-5 text-sky-600`} />
            </button>
          )}
        </Modal.Header>
        <Modal.Body>

            {/* Processing Banner */}
            {refreshing && jobStatus && (
              <div className="bg-sky-50 border-b border-sky-200 rounded-lg p-6 m-8">
                <div className="space-y-4 text-center">
                  <Loader2 className="w-8 h-8 text-sky-600 animate-spin text-center mx-auto" />
                  <p className="text-lg font-bold text-sky-900">
                    {jobStatus === 'waiting' && 'We are processing and researching...'}
                    {jobStatus === 'active' && 'Analyzing Google search rankings?...'}
                  </p>
                  <p className="text-xs text-sky-600 mt-1">
                    This runs in the background and could take a few minutes. You can leave and come back.
                  </p>
                </div>
              </div>
            )}

            {/* Modal Content */}
            {!refreshing && (
              <div className="p-6 space-y-6">
              {/* Stats Overview */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-sky-50 p-4 rounded-lg">
                  <div className="text-xs text-sky-600 font-medium uppercase tracking-wide mb-1">
                    Avg Position
                  </div>
                  <div className="text-2xl font-bold text-sky-700">
                    {typeof rankings?.avgPosition === 'number' ? `#${rankings.avgPosition.toFixed(1)}` : 'N/A'}
                  </div>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-xs text-blue-600 font-medium uppercase tracking-wide mb-1">
                    Visibility Rate
                  </div>
                  <div className="text-2xl font-bold text-blue-700">
                    {(rankings?.searchFrequency || 0).toFixed(1)}%
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-xs text-purple-600 font-medium uppercase tracking-wide mb-1">
                    Featured Snippets
                  </div>
                  <div className="text-2xl font-bold text-purple-700">
                    {rankings?.featuredSnippets || 0}
                  </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-xs text-orange-600 font-medium uppercase tracking-wide mb-1">
                    Ranking Score
                  </div>
                  <div className="text-2xl font-bold text-orange-700">
                    {rankings?.score || 0}/100
                  </div>
                </div>
              </div>

              {/* Knowledge Panel */}
              {rankings?.knowledgePanel && (
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center gap-3">
                    <Award className="w-8 h-8 text-green-600" />
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-1">
                        <Check className="w-4 h-4 text-green-600" />
                        <span>Google Knowledge Panel Detected</span>
                      </h3>
                      <p className="text-xs text-gray-600">
                        Your brand has a Knowledge Panel, giving you premium visibility in search results!
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Simplified Brand vs Non-Brand Performance */}
              {rankings?.sampleQueries && (() => {
                // Split queries into branded and non-branded
                const brandedQueries = rankings.sampleQueries.filter(q =>
                  brandName ? q.query.toLowerCase().includes(brandName.toLowerCase()) : false
                );
                const nonBrandedQueries = rankings.sampleQueries.filter(q =>
                  brandName ? !q.query.toLowerCase().includes(brandName.toLowerCase()) : true
                );

                // Calculate stats for branded searches
                const brandedMentioned = brandedQueries.filter(q => q.mentioned);
                const brandedAvgPosition = brandedMentioned.length > 0
                  ? brandedMentioned.reduce((sum, q) => sum + (q.position || 0), 0) / brandedMentioned.length
                  : null;
                const brandedVisibility = brandedQueries.length > 0
                  ? (brandedMentioned.length / brandedQueries.length) * 100
                  : 0;

                // Calculate stats for non-branded searches
                const nonBrandedMentioned = nonBrandedQueries.filter(q => q.mentioned);
                const nonBrandedAvgPosition = nonBrandedMentioned.length > 0
                  ? nonBrandedMentioned.reduce((sum, q) => sum + (q.position || 0), 0) / nonBrandedMentioned.length
                  : null;
                const nonBrandedVisibility = nonBrandedQueries.length > 0
                  ? (nonBrandedMentioned.length / nonBrandedQueries.length) * 100
                  : 0;

                return (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Globe className="w-4 h-4" />
                      <span>Search Performance Overview</span>
                    </h3>

                    {/* Two-Column Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Brand Search Card */}
                      <div className={`border rounded-lg p-5 ${
                        brandedVisibility >= 75 ? 'bg-green-50 border-green-200' :
                        brandedVisibility >= 50 ? 'bg-blue-50 border-blue-200' :
                        brandedVisibility >= 25 ? 'bg-yellow-50 border-yellow-200' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-base font-semibold text-gray-900">Brand Search</h4>
                          {brandedVisibility >= 75 && (
                            <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded-full font-medium">
                              Winning
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-gray-600 mb-4">
                          How you rank when your brand name is in the search query
                        </p>

                        <div className="grid grid-cols-3 gap-4 mb-4">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Avg Position</div>
                            <div className="text-2xl font-bold text-gray-900">
                              {brandedAvgPosition ? `#${brandedAvgPosition.toFixed(1)}` : 'N/A'}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Visibility</div>
                            <div className="text-2xl font-bold text-gray-900">
                              {brandedVisibility.toFixed(0)}%
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Trend</div>
                            <div className="text-2xl font-bold text-gray-900">
                              {renderBrandTrend()}
                            </div>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                          <div
                            className={`h-2 rounded-full ${
                              brandedVisibility >= 75 ? 'bg-green-500' :
                              brandedVisibility >= 50 ? 'bg-blue-500' :
                              brandedVisibility >= 25 ? 'bg-yellow-500' :
                              'bg-gray-400'
                            }`}
                            style={{ width: `${brandedVisibility}%` }}
                          />
                        </div>

                        <div className="text-xs text-gray-500">
                          Listed in {brandedMentioned.length} of {brandedQueries.length} branded searches
                        </div>
                      </div>

                      {/* Non-Brand Search Card */}
                      <div className={`border rounded-lg p-5 ${
                        nonBrandedVisibility >= 75 ? 'bg-green-50 border-green-200' :
                        nonBrandedVisibility >= 50 ? 'bg-blue-50 border-blue-200' :
                        nonBrandedVisibility >= 25 ? 'bg-yellow-50 border-yellow-200' :
                        'bg-gray-50 border-gray-200'
                      }`}>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-base font-semibold text-gray-900">Non-Brand Search</h4>
                          {nonBrandedVisibility >= 50 && (
                            <span className="text-xs px-2 py-0.5 bg-green-600 text-white rounded-full font-medium">
                              Strong
                            </span>
                          )}
                        </div>

                        <p className="text-xs text-gray-600 mb-4">
                          How you rank for general industry and category searches
                        </p>

                        {nonBrandedQueries.length > 0 ? (
                          <>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Avg Position</div>
                                <div className="text-2xl font-bold text-gray-900">
                                  {nonBrandedAvgPosition ? `#${nonBrandedAvgPosition.toFixed(1)}` : 'N/A'}
                                </div>
                              </div>
                              <div>
                                <div className="text-xs text-gray-500 mb-1">Visibility</div>
                                <div className="text-2xl font-bold text-gray-900">
                                  {nonBrandedVisibility.toFixed(0)}%
                                </div>
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                              <div
                                className={`h-2 rounded-full ${
                                  nonBrandedVisibility >= 75 ? 'bg-green-500' :
                                  nonBrandedVisibility >= 50 ? 'bg-blue-500' :
                                  nonBrandedVisibility >= 25 ? 'bg-yellow-500' :
                                  'bg-gray-400'
                                }`}
                                style={{ width: `${nonBrandedVisibility}%` }}
                              />
                            </div>

                            <div className="text-xs text-gray-500">
                              Listed in {nonBrandedMentioned.length} of {nonBrandedQueries.length} non-branded searches
                            </div>
                          </>
                        ) : (
                          <div className="text-center py-8">
                            <Search className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-xs text-gray-500">No non-branded searches tracked yet</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* QUICK WINS - Page 2 Rankings (11-20) */}
              {rankings?.sampleQueries && (() => {
                const page2Rankings = rankings.sampleQueries
                  .filter(q => q.mentioned && q.position && q.position >= 11 && q.position <= 20)
                  .sort((a, b) => (a.position || 0) - (b.position || 0))
                  .slice(0, 5);

                if (page2Rankings.length === 0) return null;

                return (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Zap className="w-4 h-4 text-yellow-600" />
                      <span>Quick Win Opportunities</span>
                    </h3>
                    <p className="text-xs text-gray-600 mb-3">
                      You're ranking on page 2 for these queries - optimize to reach page 1!
                    </p>
                    <div className="space-y-3">
                      {page2Rankings.map((query, idx) => (
                        <div
                          key={idx}
                          className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg p-4"
                        >
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <p className="text-sm font-medium text-gray-900">
                              "{query.query}"
                            </p>
                            <span className="text-lg font-bold text-yellow-600 whitespace-nowrap">
                              #{query.position}
                            </span>
                          </div>

                          {query.url && (
                            <a
                              href={query.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline flex items-center gap-1 mb-2"
                            >
                              {query.url}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}

                          <div className="flex items-center gap-2 mb-3">
                            <span className="text-xs px-2 py-1 bg-white border border-yellow-300 text-yellow-700 rounded-full">
                              {contextLabels[query.context] || query.context}
                            </span>
                          </div>

                          <div className="bg-white rounded-lg p-3 border border-amber-200">
                            <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                              <Rocket className="w-3 h-3" />
                              <span>Quick Win Strategy:</span>
                            </p>
                            <p className="text-xs text-gray-600">
                              Optimize your existing page at this URL with richer content, better keywords, and stronger on-page SEO. You're close to page 1!
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {/* All Search Queries with Tabs */}
              {rankings?.sampleQueries && rankings?.sampleQueries.length > 0 && (() => {
                // Split queries into branded and non-branded
                const brandedQueries = rankings.sampleQueries.filter(q =>
                  brandName ? q.query.toLowerCase().includes(brandName.toLowerCase()) : false
                );
                const nonBrandedQueries = rankings.sampleQueries.filter(q =>
                  brandName ? !q.query.toLowerCase().includes(brandName.toLowerCase()) : true
                );

                const displayedQueries = queryTab === 'brand' ? brandedQueries : nonBrandedQueries;

                return (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Search className="w-4 h-4" />
                      <span>AI Search Queries</span>
                    </h3>

                    {/* Tabs */}
                    <div className="flex border-b border-gray-200 mb-4">
                      <button
                        onClick={() => setQueryTab('brand')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          queryTab === 'brand'
                            ? 'border-sky-500 text-sky-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Tag className="w-4 h-4" />
                        Brand Queries
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          queryTab === 'brand' ? 'bg-sky-100 text-sky-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {brandedQueries.length}
                        </span>
                      </button>
                      <button
                        onClick={() => setQueryTab('non-brand')}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                          queryTab === 'non-brand'
                            ? 'border-purple-500 text-purple-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        <Globe className="w-4 h-4" />
                        Non-Brand Queries
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          queryTab === 'non-brand' ? 'bg-purple-100 text-purple-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {nonBrandedQueries.length}
                        </span>
                      </button>
                    </div>

                    {/* Tab Description */}
                    <p className="text-xs text-gray-600 mb-3">
                      {queryTab === 'brand'
                        ? 'Searches that include your brand name - tracking how you rank when people search for you directly.'
                        : 'Industry and category searches without your brand name - tracking your discoverability.'
                      }
                    </p>

                    {displayedQueries.length > 0 ? (
                      <div className="space-y-2 max-h-[500px] overflow-y-auto bg-gray-50 rounded-lg p-3 border border-gray-200">
                        {displayedQueries
                          .sort((a, b) => {
                            // Sort: mentioned first, then by position
                            if (a.mentioned && !b.mentioned) return -1;
                            if (!a.mentioned && b.mentioned) return 1;
                            if (a.mentioned && b.mentioned) {
                              return (a.position || 999) - (b.position || 999);
                            }
                            return 0;
                          })
                          .map((query, idx) => (
                            <div
                              key={idx}
                              className={`border rounded-lg p-3 bg-white ${
                                query.mentioned
                                  ? 'border-green-300 shadow-sm'
                                  : 'border-gray-200'
                              }`}
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-gray-900 mb-2">
                                    "{query.query}"
                                  </p>
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {query.mentioned ? (
                                      <>
                                        <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full font-medium flex items-center gap-1">
                                          <Check className="w-3 h-3" />
                                          <span>Listed</span>
                                        </span>
                                        {query.position !== null && (
                                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                                            Position #{query.position}
                                          </span>
                                        )}
                                        {query.featured && (
                                          <span className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full font-medium flex items-center gap-1">
                                            <Star className="w-3 h-3" />
                                            <span>Featured</span>
                                          </span>
                                        )}
                                      </>
                                    ) : (
                                      <span className="text-xs px-2 py-1 bg-red-100 text-red-600 rounded-full">
                                        Not listed
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200">
                        {queryTab === 'brand' ? (
                          <>
                            <Tag className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm text-gray-500">No brand queries tracked yet</p>
                          </>
                        ) : generatedNonBrandQueries.length > 0 ? (
                          // Show generated queries when available
                          <div className="text-left p-4 space-y-3">
                            <div className="flex items-center justify-between mb-3">
                              <h4 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-purple-500" />
                                Suggested Non-Brand Queries
                              </h4>
                              <button
                                onClick={handleGenerateNonBrandQueries}
                                disabled={generatingQueries}
                                className="text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1"
                              >
                                <RefreshCw className={`w-3 h-3 ${generatingQueries ? 'animate-spin' : ''}`} />
                                Regenerate
                              </button>
                            </div>
                            <p className="text-xs text-gray-500 mb-4">
                              Real queries from Google's "People Also Ask" and Related Searches. Track these to measure your discoverability.
                            </p>
                            <div className="space-y-2 max-h-[400px] overflow-y-auto">
                              {generatedNonBrandQueries.map((q, idx) => (
                                <div
                                  key={idx}
                                  className="bg-white border border-purple-200 rounded-lg p-3 hover:border-purple-400 transition-colors"
                                >
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <p className="text-sm font-medium text-gray-900">"{q.query}"</p>
                                    {q.source === 'google_paa' && (
                                      <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full flex items-center gap-1">
                                        <Search className="w-3 h-3" />
                                        Google PAA
                                      </span>
                                    )}
                                    {q.source === 'google_related' && (
                                      <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full flex items-center gap-1">
                                        <Search className="w-3 h-3" />
                                        Google Related
                                      </span>
                                    )}
                                    {q.source === 'ai_generated' && (
                                      <span className="flex-shrink-0 text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full flex items-center gap-1">
                                        <Sparkles className="w-3 h-3" />
                                        AI Generated
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-xs text-gray-500">{q.reasoning}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <>
                            <Globe className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                            <p className="text-sm text-gray-500 mb-2">No non-brand queries tracked yet</p>
                            <p className="text-xs text-gray-400 mb-4">
                              Non-brand queries help you understand how visible you are for industry-related searches.
                            </p>
                            <button
                              onClick={handleGenerateNonBrandQueries}
                              disabled={generatingQueries}
                              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50"
                            >
                              {generatingQueries ? (
                                <>
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                <>
                                  <Sparkles className="w-4 h-4" />
                                  Generate Non-Brand Queries
                                </>
                              )}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })()}

            </div>
          )}
        </Modal.Body>
      </Modal>

      {/* Trend Modal */}
      <TrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        title="Google Rankings Trend"
        metricName="Average Position"
        data={trendData}
        color="#0ea5e9"
        unit=""
      />
    </>
  )
}
